import { Injectable } from '@angular/core';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import { GroupService } from 'app/providers/semantria-api/group.service';

/**
 * Service responsible for opening Spotlight Agent (Next app)
 * with SSO token and project / collection context.
 */
const DEFAULT_SPOTLIGHT_AGENT_URL =
  'https://spotlight-agent-preprod-b6b3c3dyg7hwhhbc.centralus-01.azurewebsites.net';

@Injectable({ providedIn: 'root' })
export class SpotlightAgentSsoService {
  constructor(
    private authService: AuthService,
    private groupService: GroupService
  ) {}

  /**
   * Extract region code (eu, au) from hostname.
   * Returns empty string if no region detected (defaults to US).
   */
  public getRegionFromOrigin(origin: string = window.location.hostname): string {
    if (origin.includes('.inmoment.eu') || origin.includes('-eu.semantria.com')) {
      return 'eu';
    }
    if (origin.includes('.inmoment.com.au') || origin.includes('-au.semantria.com')) {
      return 'au';
    }
    return '';
  }

  /**
   * Detect Spotlight environment (dev, preprod, prod) from hostname.
   */
  public getEnvironmentFromOrigin(origin: string = window.location.hostname): string {
    const appConfig: any = (window as any).appConfig || {};
    const configuredEnv = appConfig.spotlightAgent?.env || appConfig.spotlightAgent?.envOverride;
    if (configuredEnv === 'dev' || configuredEnv === 'preprod' || configuredEnv === 'prod') {
      return configuredEnv;
    }
    if (origin === 'localhost' || origin === '127.0.0.1' || origin.includes('dev')) {
      return 'dev';
    }
    if (origin.includes('preprod')) {
      return 'preprod';
    }
    return 'preprod';
  }

  public getAgentBaseUrl(): string {
    const appConfig: unknown = (window as any).appConfig;
    const configuredUrl = (appConfig as { spotlightAgent?: { url?: string } })?.spotlightAgent?.url;
    if (configuredUrl) {
      return configuredUrl;
    }
    return DEFAULT_SPOTLIGHT_AGENT_URL;
  }

  /**
   * Open Spotlight Agent in a new tab using SSO token and context.
   *
   * @param target Route segment after /app (e.g. 'dashboard', 'chat').
   * @param projectId Current project identifier.
   * @param collectionId Current collection identifier (e.g. collectionName).
   */
  public async openWithContext(
    target: string,
    projectId: string,
    collectionId: string
  ): Promise<void> {
    const session = await this.authService.getSession();
    const baseUrl = this.getAgentBaseUrl();
    const url = new URL('/sso', baseUrl);

    url.searchParams.set('token', session.id);
    url.searchParams.set('projectId', projectId);
    url.searchParams.set('collectionId', collectionId);

    const groupId = this.groupService.getSelectedGroupId(true);
    if (groupId) {
      url.searchParams.set('groupId', groupId);
    }

    const region = this.getRegionFromOrigin();
    if (region) {
      url.searchParams.set('region', region);
    }

    const env = this.getEnvironmentFromOrigin();
    url.searchParams.set('env', env);

    url.searchParams.set('target', target);
    window.open(url.toString(), '_blank');
  }
}
